import { FieldArray } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect } from 'react-redux';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import { useFormState } from 'react-final-form';

import { PM_REJECT } from 'CorePackage/MainModule/routes/routes';
import {
  getObjectResultBlockId,
  getPmRejectBlockId,
  PM_REJECT_TITLE_BLOCK_ID,
} from 'KnmPackage/MainModule/constants/fieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getKnmFormRef,
  getKnmIsNew,
  getKnmStatusCode,
  getKnmWorkDaysCountBeforeStart,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Section } from 'UIPackage/FormModule/components/Section';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { getCatalogsPmCanceledReasonsSelectOptions } from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IKnmPmForm } from 'KnmPmPackage/MainModule/interfaces/IKnmPmForm';
import { KnmErknmStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusCodesEnum';
import { KnmErpStatusCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmErpStatusCodesEnum';

import { IPmRejectForm } from '../interfaces/IPmRejectForm';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { makeRequired } from '../../../CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { IPmCanceledReasonSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IPmCanceledReasonSelectOption';
import styles from './PmReject.module.scss';

interface IPmRejectProps {
  watchOnly: boolean;
  pmReject: IPmRejectForm[] | undefined;
  isUnableToRefuse?: boolean | undefined;
}

interface IPmRejectStoreProps {
  formRef: IKnmFormRef | undefined;
  workDaysCountBeforeStart?: number;
  pmCanceledReasonsOptions?: IPmCanceledReasonSelectOption[];
  isNewPm?: boolean;
  statusCode: KnmErpStatusCodesEnum | KnmErknmStatusCodesEnum | undefined;
}

const PmRejectWithoutStore = ({
  watchOnly,
  formRef,
  pmReject,
  isUnableToRefuse,
  workDaysCountBeforeStart,
  pmCanceledReasonsOptions,
  isNewPm,
  statusCode,
}: IPmRejectProps & IPmRejectStoreProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const { values } = useFormState<IKnmPmForm>({
    subscription: { values: true },
  });

  const addKnmResult = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.checked) {
        if (push) {
          push('pmReject', {
            listId: uuidv4(),
            isNew: true,
            date: undefined,
          });
        }
      } else if (remove) {
        remove('pmReject', 0);
      }
    },
    [push, remove, watchOnly],
  );

  /**
   * Если есть рассчитанный период workDaysCountBeforeStart и наличие pmCanceledReasonsOptions, то
   * проводим проверку. Если все элементы не попадают в период, то блокируем поле
   */
  const isDisableByWorkDays = useMemo(
    () =>
      (pmCanceledReasonsOptions?.length || 0) > 0 && typeof workDaysCountBeforeStart === 'number'
        ? pmCanceledReasonsOptions?.every(item => item.maxDaysBeforeStart >= workDaysCountBeforeStart)
        : false,
    [workDaysCountBeforeStart],
  );

  /**
   * Очистка если сменили дату начала мероприятия, и флаг блокировки сменился на true
   * и это отказ не EPGU
   */
  useEffect(() => {
    const reject = formRef?.current.getFieldState('pmReject')?.value?.[0];

    if (
      isNewPm &&
      remove &&
      reject?.canceledReason?.code !== 'EPGU' &&
      typeof reject?.canceledReason?.maxDaysBeforeStart === 'number' &&
      typeof workDaysCountBeforeStart === 'number' &&
      reject?.canceledReason?.maxDaysBeforeStart < workDaysCountBeforeStart
    ) {
      remove('pmReject', 0);
    }
  }, [values.startDate]);

  /**
   * Функция фильтрации значений для поля "Причина непроведения профилактического мероприятия"
   */
  const canceledReasonsTransformFunc = useCallback((items: IPmCanceledReasonSelectOption[], isNew: boolean) => {
    if (!items) {
      return [];
    }

    const filteredByWorkDays = workDaysCountBeforeStart
      ? items.filter(item => item.maxDaysBeforeStart < workDaysCountBeforeStart)
      : items;

    if (isNew) {
      return filteredByWorkDays.filter(item => item.id !== 4);
    }

    return filteredByWorkDays;
  }, []);

  const isDisabledByStatus =
    statusCode !== KnmErknmStatusCodesEnum.TYPE_WAITING_CARRY_OUT &&
    statusCode !== KnmErknmStatusCodesEnum.TYPE_CARRIED_OUT &&
    statusCode !== KnmErknmStatusCodesEnum.TYPE_COMPLETED;

  const isRejectDisabled =
    watchOnly ||
    (!!pmReject?.length && !pmReject?.filter(i => i.isNew).length) ||
    isUnableToRefuse ||
    isDisableByWorkDays ||
    workDaysCountBeforeStart === undefined ||
    isDisabledByStatus;

  return (
    <Section
      title="Информация о непроведении (несогласии) ПМ"
      id={PM_REJECT}
      titleId={PM_REJECT_TITLE_BLOCK_ID}
      type="list"
      customButton={
        <Checkbox
          id={`pmRejectedCheckbox`}
          theme="system"
          type="checkbox"
          className={styles.Checkbox}
          onChange={addKnmResult}
          disabled={isRejectDisabled}
          checked={!!pmReject?.length}
        />
      }
    >
      <FieldArray name="pmReject">
        {({ fields }) => {
          if (!fields.length) {
            return null;
          }

          return fields.map((name, index) => {
            const fieldValue: IPmRejectForm = fields.value?.[index];
            const id = getObjectResultBlockId(fieldValue.listId);
            const { isNew } = fieldValue;

            return (
              <Row isListItem notSpace key={id} id={getPmRejectBlockId(id)}>
                <FieldBlock label="Причина непроведения профилактического мероприятия">
                  <SelectInputFieldAutoOptions
                    id={`${name}.canceledReason`}
                    name={`${name}.canceledReason`}
                    size="medium"
                    optionName={CommonCatalogsNamesEnum.PM_CANCELED_REASONS}
                    transformFunc={(items: IPmCanceledReasonSelectOption[]) =>
                      canceledReasonsTransformFunc(items, isNew)
                    }
                    validate={makeRequired(REQUIRED_ERROR)}
                    disabled={!isNew}
                  />
                </FieldBlock>
              </Row>
            );
          });
        }}
      </FieldArray>
    </Section>
  );
};

const mapStateToProps = (state: IRootState): IPmRejectStoreProps => ({
  formRef: getKnmFormRef(state),
  workDaysCountBeforeStart: getKnmWorkDaysCountBeforeStart(state),
  pmCanceledReasonsOptions: getCatalogsPmCanceledReasonsSelectOptions(state),
  isNewPm: getKnmIsNew(state),
  statusCode: getKnmStatusCode(state),
});

export const PmReject = connect(mapStateToProps)(PmRejectWithoutStore);
